import {
  Badge,
  Box,
  ButtonGroup,
  Divider,
  Flex,
  Link,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { FormProvider } from "react-hook-form";

import { Restricted } from "@/app/components/Restricted";
import FormInput from "@/common/components/Form/FormInput";
import FormReset from "@/common/components/Form/FormReset";
import { FormRootError } from "@/common/components/Form/FormRootError";
import FormSelect from "@/common/components/Form/FormSelect";
import FormSubmit from "@/common/components/Form/FormSubmit";
import { SectionHeading } from "@/common/components/SectionHeading";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { commonQueryKeys } from "@/common/data/commonQueryKeys";
import useApiForm from "@/common/hooks/useApiForm";
import api from "@/common/utils/api";
import OrganizationLogoPicker from "@/global-settings/components/OrganizationLogoPicker";
import {
  PatchOrgArgsSchema,
  PatchOrgArgsType,
} from "@reflag/shared/organizationAPI";
import { KeyFormatPatterns, KeyFormats } from "@reflag/shared/utils/keyFormat";

export default function GeneralOrganization() {
  const { org } = useEnvironment();

  const toast = useToast();
  const queryClient = useQueryClient();

  const { form, handleSubmit } = useApiForm(
    (data: PatchOrgArgsType) =>
      api
        .patch<"/orgs/:orgId">(`/orgs/${org.id}`, data)
        .then((res) => res.data.org),
    PatchOrgArgsSchema,
    {
      onSuccess: (org) => {
        toast({
          title: "Settings saved",
          status: "success",
        });
        queryClient.invalidateQueries({
          queryKey: commonQueryKeys.bootstrap(org.id),
        });
        form.reset({
          name: org.name,
          logoUrl: org.logoUrl,
          featureKeyFormat: org.featureKeyFormat,
        });
      },
    },
    {
      defaultValues: {
        name: org.name,
        logoUrl: org.logoUrl,
        featureKeyFormat: org.featureKeyFormat,
      },
    },
  );

  const featureKeyFormat = form.watch("featureKeyFormat") ?? "custom";
  return (
    <Flex direction="column" gap={4} maxW="mediumForm">
      <Restricted alertStyle="callout" role="admin">
        <Flex direction="column" gap={4}>
          <form onSubmit={handleSubmit}>
            <FormProvider {...form}>
              <VStack align="flex-start" spacing={4}>
                <FormInput
                  autoComplete="organization"
                  label="Organization name"
                  maxLength={40}
                  name="name"
                />

                <OrganizationLogoPicker />

                <FormSelect
                  label="Flag key naming convention"
                  name="featureKeyFormat"
                  options={KeyFormats.map((format) => ({
                    label: KeyFormatPatterns[format].label,
                    value: format,
                  }))}
                />
                <Text color="dimmed" fontSize="sm">
                  {KeyFormatPatterns[featureKeyFormat].description}
                </Text>
                <FormRootError />
                <ButtonGroup>
                  <FormSubmit showPostSubmit />
                  <FormReset />
                </ButtonGroup>
              </VStack>
            </FormProvider>
          </form>
          <Divider />

          <VStack align="flex-start">
            <Flex direction="column" gap={2}>
              <SectionHeading>Data residency</SectionHeading>
              <Box>
                <Badge>{org.isGlobal ? "Global" : "Europe"}</Badge>
              </Box>
            </Flex>
            <Box fontSize="sm">
              Contact us at{" "}
              <Link href="mailto:hello@reflag.com">hello@reflag.com</Link> to
              change your data residency.
            </Box>
          </VStack>
        </Flex>
      </Restricted>
    </Flex>
  );
}
